import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, User, IdCard, FileText, Loader2, MapPin, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { getToken } from '../services/tokenService';
import PieceIdentiteMO from '../pages/CalculateurBATICO/PieceIdentiteMO';

const BASE = process.env.REACT_APP_BACKEND_URL;
const h = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fmt = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const eur = (v) => (v ? `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '—');

export const ouvrirDocGed = async (id) => {
  try {
    const r = await axios.get(`${BASE}/api/ged/download/${id}?mode=view`, { ...h(), responseType: 'blob' });
    window.open(URL.createObjectURL(r.data), '_blank', 'noopener');
  } catch { toast.error('Document indisponible'); }
};

const Bloc = ({ icon: Icon, titre, children, testId }) => (
  <section className="space-y-2" data-testid={testId}>
    <h3 className="flex items-center gap-1.5 text-sm font-bold text-[#0a3d62]"><Icon className="h-4 w-4" /> {titre}</h3>
    {children}
  </section>
);

const Chantiers = ({ items }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200">
    <table className="w-full text-left text-xs">
      <thead className="bg-slate-50 text-slate-500"><tr><th className="px-2 py-1.5">Référence</th><th className="px-2 py-1.5">Nature</th><th className="px-2 py-1.5">Opération</th><th className="px-2 py-1.5">Montant TTC</th><th className="px-2 py-1.5">Date</th></tr></thead>
      <tbody>
        {items.map((c) => (
          <tr key={c.reference} className="border-t border-slate-100" data-testid={`fiche-mo-chantier-${c.reference}`}>
            <td className="px-2 py-1.5 font-mono">{c.reference}</td>
            <td className="px-2 py-1.5">{c.nature}{c.statut ? <span className="ml-1 text-slate-400">· {c.statut}</span> : null}</td>
            <td className="px-2 py-1.5">{c.operation || '—'}{c.adresse ? <p className="text-slate-400">{c.adresse}</p> : null}</td>
            <td className="px-2 py-1.5">{eur(c.montant_ttc)}</td>
            <td className="px-2 py-1.5">{fmt(c.date)}</td>
          </tr>
        ))}
        {!items.length && <tr><td colSpan={5} className="px-2 py-4 text-center text-slate-400">Aucun chantier</td></tr>}
      </tbody>
    </table>
  </div>
);

/** Fiche détaillée d'un maître d'ouvrage : coordonnées, chantiers, membres, documents. */
export default function FicheMaitreOuvrage({ ficheId, onClose, onUpdate }) {
  const [f, setF] = useState(null);
  const [membreId, setMembreId] = useState('');
  const charger = useCallback(() => axios.get(`${BASE}/api/registre-maitres-ouvrage/${ficheId}`, h())
    .then((r) => { setF(r.data); setMembreId((v) => v || r.data.membres?.[0]?.id || ''); })
    .catch((e) => { toast.error(e?.response?.data?.detail || 'Fiche indisponible'); onClose(); }), [ficheId, onClose]);
  useEffect(() => {
    if (!ficheId) return;
    setF(null); setMembreId('');
    charger();
  }, [ficheId, charger]);
  const apresDepot = () => { charger(); if (onUpdate) onUpdate(); };
  const pro = f?.type === 'professionnel';
  return (
    <Dialog open={!!ficheId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="fiche-mo">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0a3d62]">
            {pro ? <Building2 className="h-5 w-5" /> : <User className="h-5 w-5" />}
            {f ? (pro ? f.raison_sociale : `${f.nom || ''} ${f.prenom || ''}`) : 'Chargement…'}
          </DialogTitle>
        </DialogHeader>
        {!f ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /> : (
          <div className="space-y-5">
            <Bloc icon={MapPin} titre="Coordonnées" testId="fiche-mo-coordonnees">
              <p className="text-sm text-slate-700">{pro ? `SIRET ${f.siret || '—'}${f.nom ? ` · Représentant : ${f.nom} ${f.prenom || ''}` : ''}` : 'Particulier'}</p>
              <p className="text-sm text-slate-600">{[f.adresse, [f.code_postal, f.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'}</p>
              <p className="text-sm text-slate-600">{[f.telephone, f.email].filter(Boolean).join(' · ') || '—'}</p>
            </Bloc>
            <Bloc icon={FileText} titre={`Chantiers (${f.chantiers_detail.length})`} testId="fiche-mo-chantiers"><Chantiers items={f.chantiers_detail} /></Bloc>
            <Bloc icon={Users} titre={`Membres (${(f.membres || []).length})`} testId="fiche-mo-membres">
              <div className="flex flex-wrap gap-1.5">{(f.membres || []).map((m) => <span key={m.id} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-700">{m.nom || m.id}</span>)}</div>
            </Bloc>
            <Bloc icon={IdCard} titre={`Documents (${f.documents.length})`} testId="fiche-mo-documents">
              {!f.pieces_identite?.length && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="fiche-mo-piece-manquante">Pièce d'identité manquante</p>}
              {(f.membres || []).length > 0 ? (
                <div className="space-y-2" data-testid="fiche-mo-depot-piece">
                  {f.membres.length > 1 && (
                    <label className="flex items-center gap-2 text-xs text-slate-600">GED du membre :
                      <select value={membreId} onChange={(e) => setMembreId(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-xs" data-testid="fiche-mo-membre-select">
                        {f.membres.map((m) => <option key={m.id} value={m.id}>{m.nom || m.id}</option>)}
                      </select>
                    </label>
                  )}
                  <PieceIdentiteMO sansRetrait membreId={membreId} onChange={apresDepot}
                    mo={{ type: f.type, raison_sociale: f.raison_sociale, siret: f.siret, nom: f.nom, prenom: f.prenom, email: f.email,
                      adresse: f.adresse, code_postal: f.code_postal, ville: f.ville, telephone: f.telephone, pieces_identite: f.pieces_identite || [] }} />
                </div>
              ) : <p className="text-xs text-slate-500">Aucun membre rattaché : la pièce ne peut pas être classée dans une GED.</p>}
              <ul className="space-y-1">
                {f.documents.map((d) => (
                  <li key={d.id}>
                    <button type="button" onClick={() => ouvrirDocGed(d.id)} className="flex w-full items-center justify-between rounded-md border border-slate-200 px-3 py-1.5 text-left text-xs hover:bg-slate-50" data-testid={`fiche-mo-doc-${d.id}`}>
                      <span className="truncate font-medium text-slate-700">{d.nom}</span><span className="ml-2 shrink-0 text-slate-400">{d.type} · {fmt(d.date)}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {(f.rappels_piece || []).length > 0 && <p className="text-xs text-slate-500">Dernier rappel : {fmt(f.rappels_piece[f.rappels_piece.length - 1].at)}</p>}
            </Bloc>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
